import { ElementType, JSX } from 'react';
import { TFunction } from 'i18next';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { UserNotification } from '@api/notifications/interfaces';
import { NotificationPresenter } from './interfaces';
import { scoreSubmissionSubmittedPresenter } from './presenters/scoreSubmissionSubmitted';
import { scoreSubmissionDecisionPresenters } from './presenters/scoreSubmissionDecision';
import { scoreSubmissionDeletedByMemberPresenter } from './presenters/scoreSubmissionDeletedByMember';
import { memberWithdrewFromCompetitionPresenter } from './presenters/memberWithdrewFromCompetition';
import { competitionStatusPresenters } from './presenters/competitionStatus';
import { competitionManagerAssignedPresenter } from './presenters/competitionManagerAssigned';
import { competitionManagerRemovedPresenter } from './presenters/competitionManagerRemoved';
import { competitionEndingSoonPresenter } from './presenters/competitionEndingSoon';
import { competitionSessionCancelledPresenter } from './presenters/competitionSessionCancelled';
import { competitionSessionNotHeldPresenter } from './presenters/competitionSessionNotHeld';
import { competitionSessionTomorrowReminderPresenter } from './presenters/competitionSessionTomorrowReminder';
import { competitionSessionTomorrowParticipantReminderPresenter } from './presenters/competitionSessionTomorrowParticipantReminder';
import { shootingClearanceGrantedPresenter } from './presenters/shootingClearanceGranted';
import { shootingClearanceRevokedPresenter } from './presenters/shootingClearanceRevoked';
import { feedInteractionPresenters } from './presenters/feedInteraction';

type RegisteredNotificationPresenter = {
  type: string;
  icon: ElementType;
  badgeIcon: ElementType | null;
  label: (payload: unknown, t: TFunction) => string | null;
  message: (payload: unknown) => JSX.Element | null;
  body: (payload: unknown) => JSX.Element | null;
  path: (payload: unknown) => string | null;
};

const register = <TPayload>(
  presenter: NotificationPresenter<TPayload>
): RegisteredNotificationPresenter => ({
  type: presenter.type,
  icon: presenter.icon,
  badgeIcon: presenter.badgeIcon ?? null,
  label: (payload, t) =>
    presenter.isPayload(payload) ? presenter.label(payload, t) : null,
  message: (payload) =>
    presenter.isPayload(payload) ? presenter.message(payload) : null,
  body: (payload) =>
    presenter.body && presenter.isPayload(payload)
      ? presenter.body(payload)
      : null,
  path: (payload) =>
    presenter.isPayload(payload) ? presenter.path(payload) : null,
});

const presenters: RegisteredNotificationPresenter[] = [
  register(scoreSubmissionSubmittedPresenter),
  register(memberWithdrewFromCompetitionPresenter),
  register(competitionManagerAssignedPresenter),
  register(competitionManagerRemovedPresenter),
  register(competitionEndingSoonPresenter),
  register(competitionSessionCancelledPresenter),
  register(competitionSessionNotHeldPresenter),
  register(competitionSessionTomorrowReminderPresenter),
  register(competitionSessionTomorrowParticipantReminderPresenter),
  register(shootingClearanceGrantedPresenter),
  register(shootingClearanceRevokedPresenter),
  ...scoreSubmissionDecisionPresenters.map((presenter) => register(presenter)),
  register(scoreSubmissionDeletedByMemberPresenter),
  ...competitionStatusPresenters.map((presenter) => register(presenter)),
  ...feedInteractionPresenters.map((presenter) => register(presenter)),
];

const findPresenter = (
  type: string
): RegisteredNotificationPresenter | undefined =>
  presenters.find((presenter) => presenter.type === type);

export const getNotificationIcon = (type: string): ElementType =>
  findPresenter(type)?.icon ?? NotificationsNoneIcon;

export const getNotificationBadgeIcon = (type: string): ElementType | null =>
  findPresenter(type)?.badgeIcon ?? null;

export const getNotificationLabel = (
  notification: UserNotification,
  t: TFunction
): string =>
  findPresenter(notification.type)?.label(notification.payload, t) ??
  t('notifications:unknown');

export const getNotificationPath = (
  notification: UserNotification
): string | null =>
  findPresenter(notification.type)?.path(notification.payload) ?? null;

export const getNotificationMessage = (
  notification: UserNotification
): JSX.Element | null =>
  findPresenter(notification.type)?.message(notification.payload) ?? null;

export const getNotificationBody = (
  notification: UserNotification
): JSX.Element | null =>
  findPresenter(notification.type)?.body(notification.payload) ?? null;
